export const ALLOCATION_MODES = ['shared', 'fixed', 'percent'] as const;
export type AllocationMode = (typeof ALLOCATION_MODES)[number];

export interface StoreAllocationInput {
  storeId: string;
  name: string;
  quotaBytes: number | null;
  quotaPercent: number | null;
  usedBytes: number;
  assetCount: number;
}

export interface StoreAllocation extends StoreAllocationInput {
  /** null = no ceiling of its own (shared mode, or an unallocated store). */
  capBytes: number | null;
  availableBytes: number | null;
  usagePercent: number | null;
  isOverCap: boolean;
}

export interface AllocationSummary {
  mode: AllocationMode;
  orgCapBytes: number;
  orgUsedBytes: number;
  stores: StoreAllocation[];
  /** Bytes of the pool not handed to any store — usable by nobody under
   *  'fixed'/'percent', which is why it is surfaced rather than hidden. */
  unallocatedBytes: number;
  /** True when the split promises more than the merchant actually has. */
  isOverAllocated: boolean;
}

/**
 * Turns a merchant's chosen split into a concrete per-store ceiling.
 *
 * Three modes, and the differences matter more than they first appear:
 *
 * - **shared** — no store has a ceiling. The first store to fill the pool
 *   takes it all, which is the right default: most merchants have one busy
 *   store and do not want to think about this at all.
 * - **fixed** — each store gets an explicit byte allowance. Predictable, but
 *   it does *not* follow a plan upgrade: raise the merchant's allowance and
 *   the extra lands in `unallocatedBytes` until they divide it.
 * - **percent** — each store gets a share. This one re-scales by itself when
 *   the platform admin raises the merchant's allowance, which is usually what
 *   a merchant means by "half each".
 *
 * A store with no allocation under fixed/percent gets a cap of 0 rather than
 * unlimited access. Treating "not configured" as "unlimited" would silently
 * let one store consume a pool the merchant had carefully divided.
 */
export function resolveAllocations(
  mode: AllocationMode,
  orgCapBytes: number,
  orgUsedBytes: number,
  stores: StoreAllocationInput[]
): AllocationSummary {
  const resolved = stores.map(store => {
    const capBytes = capForStore(mode, orgCapBytes, store);
    return {
      ...store,
      capBytes,
      availableBytes: capBytes === null ? null : Math.max(0, capBytes - store.usedBytes),
      usagePercent:
        capBytes === null || capBytes === 0
          ? null
          : Number(((store.usedBytes / capBytes) * 100).toFixed(1)),
      isOverCap: capBytes !== null && store.usedBytes > capBytes,
    };
  });

  const allocated = resolved.reduce((sum, s) => sum + (s.capBytes ?? 0), 0);

  return {
    mode,
    orgCapBytes,
    orgUsedBytes,
    stores: resolved,
    unallocatedBytes: mode === 'shared' ? 0 : Math.max(0, orgCapBytes - allocated),
    isOverAllocated: mode !== 'shared' && allocated > orgCapBytes,
  };
}

function capForStore(mode: AllocationMode, orgCapBytes: number, store: StoreAllocationInput): number | null {
  if (mode === 'shared') return null;
  if (mode === 'fixed') return store.quotaBytes == null ? 0 : Number(store.quotaBytes);
  // Percent, floored: handing out ceil() shares of a pool lets the rounding
  // add up to more than the merchant owns.
  return Math.floor((orgCapBytes * (store.quotaPercent ?? 0)) / 100);
}

export interface AllocationValidationError {
  message: string;
}

/**
 * Rejects a split the merchant cannot actually honour.
 *
 * Over-allocation is refused rather than accepted-and-warned: a 1 GB pool
 * divided into two 800 MB stores reads on screen as 1.6 GB of headroom, and
 * the merchant only discovers otherwise when the second store fails an upload
 * at what its own bar claims is 50% full.
 */
export function validateAllocation(
  mode: AllocationMode,
  orgCapBytes: number,
  stores: Array<{ storeId: string; quotaBytes?: number | null; quotaPercent?: number | null; usedBytes: number }>
): AllocationValidationError | null {
  if (mode === 'shared') return null;

  if (mode === 'percent') {
    const total = stores.reduce((sum, s) => sum + (s.quotaPercent ?? 0), 0);
    if (total > 100) {
      return { message: `Store shares add up to ${total}%. They cannot exceed 100%.` };
    }
    for (const store of stores) {
      const percent = store.quotaPercent ?? 0;
      if (percent < 0 || percent > 100) {
        return { message: 'Each store share must be between 0 and 100%.' };
      }
    }
    return null;
  }

  const total = stores.reduce((sum, s) => sum + (s.quotaBytes ?? 0), 0);
  if (total > orgCapBytes) {
    return {
      message:
        `The amounts add up to more storage than your plan includes ` +
        `(${formatMb(total)} of ${formatMb(orgCapBytes)}). Reduce a store or upgrade your plan.`,
    };
  }
  for (const store of stores) {
    if ((store.quotaBytes ?? 0) < 0) {
      return { message: 'A store allowance cannot be negative.' };
    }
    // Allocating a store less than it already holds is refused: it would put
    // the store instantly over its own limit with no way to upload until
    // someone deletes media, which is a confusing state to hand a merchant.
    if (store.quotaBytes != null && store.quotaBytes < store.usedBytes) {
      return {
        message:
          `One store already uses ${formatMb(store.usedBytes)}, more than the ` +
          `${formatMb(store.quotaBytes)} you are giving it. Free up space there first.`,
      };
    }
  }
  return null;
}

function formatMb(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
